import { getAccessToken } from '../authentication/supabaseService';

export type AccountProductFavoritesScope = 'products' | 'utilization';

export async function fetchAccountProductFavorites(scope: AccountProductFavoritesScope = 'products'): Promise<string[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/account-product-favorites?scope=${scope}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to fetch account product favorites: ${res.status}`);
  return res.json();
}

export async function setAccountProductFavorite(key: string, favorite: boolean, scope: AccountProductFavoritesScope = 'products'): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch('/api/account-product-favorites', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ key, favorite, scope }),
  });
  if (!res.ok) throw new Error(`Failed to save account product favorite: ${res.status}`);
}

export type WeekDay =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export const WEEK_DAYS: WeekDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const DEFAULT_WEEKLY_REPORT_SEND_DAY: WeekDay = 'monday';

export interface BackendRestaurant {
  id: string;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  // report_settings rides along in the /getAll payload (SELECT *), so the
  // restaurants load already carries it — no separate report-settings fetch.
  report_settings?: ReportSettings | null;
  // Per-restaurant weekly-report send day (own column; default 'monday').
  weekly_report_send_day?: WeekDay;
  // Optional restaurant logo — full public S3 URL, null when none is uploaded.
  logo_url?: string | null;
}

export interface FrontendRestaurant {
  id: string;
  userId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  // Cached report settings (cost targets, waste %, tracked dishes). Present from
  // the restaurants load; cost-price/dashboard/subrecipe consumers read this
  // instead of re-fetching the report-settings endpoint.
  reportSettings?: ReportSettings | null;
  // Optional restaurant logo (public URL). null = render the initial-circle fallback.
  logoUrl: string | null;
}

function mapBackendToFrontend(r: BackendRestaurant): FrontendRestaurant {
  // Merge the standalone send-day column into the cached report settings so
  // profile chips can show it without a separate fetch.
  const baseSettings = r.report_settings ?? { trackedDishes: { food: [], drinks: [] } };
  return {
    id: r.id,
    userId: r.user_id,
    name: r.name,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    logoUrl: r.logo_url ?? null,
    reportSettings: { ...baseSettings, weeklyReportSendDay: r.weekly_report_send_day },
  };
}

export async function createRestaurant(
  name: string
): Promise<FrontendRestaurant> {
  const token = await getAccessToken();
  const res = await fetch('/api/restaurants/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ name }),
  });

  if (!res.ok) {
    throw new Error(`Failed to create restaurant: ${res.statusText}`);
  }

  const backend: BackendRestaurant = await res.json();
  return mapBackendToFrontend(backend);
}

export async function fetchRestaurants(): Promise<FrontendRestaurant[]> {
  const token = await getAccessToken();
  const res = await fetch('/api/restaurants/getAll', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch restaurants: ${res.status} ${res.statusText}`);
  }

  const backendList: BackendRestaurant[] = await res.json();
  return backendList.map(mapBackendToFrontend);
}

export async function updateRestaurant(
  id: string,
  name: string
): Promise<FrontendRestaurant> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurants/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ name }),
  });

  if (!res.ok) {
    throw new Error(`Failed to update restaurant: ${res.statusText}`);
  }

  const backend: BackendRestaurant = await res.json();
  return mapBackendToFrontend(backend);
}

/**
 * Upload (or replace) the restaurant logo. Multipart — no Content-Type header on
 * purpose so the browser sets the multipart boundary itself (userService pattern).
 * Admin-only server-side; returns the updated restaurant row.
 */
export async function uploadRestaurantLogo(
  id: string,
  logo: File
): Promise<FrontendRestaurant> {
  const token = await getAccessToken();
  const formData = new FormData();
  formData.append('logo', logo);

  const res = await fetch(`/api/restaurants/${id}/logo`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || `Failed to upload restaurant logo: ${res.statusText}`);
  }

  const backend: BackendRestaurant = await res.json();
  return mapBackendToFrontend(backend);
}

/**
 * Remove the restaurant logo (S3 object + column). Returns the updated row.
 */
export async function deleteRestaurantLogo(id: string): Promise<FrontendRestaurant> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurants/${id}/logo`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || `Failed to remove restaurant logo: ${res.statusText}`);
  }

  const backend: BackendRestaurant = await res.json();
  return mapBackendToFrontend(backend);
}

export async function deleteRestaurant(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurants/delete/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to delete restaurant: ${res.statusText}`);
  }
}

export interface ReportSettings {
  trackedDishes: {
    food: string[];
    drinks: string[];
  };
  sections?: {
    snapshotOverview?: boolean;
    dishPerformance?: boolean;
    dailyPerformance?: boolean;
    topSellingItems?: boolean;
    bottomSellingItems?: boolean;
    highestSpendingProducts?: boolean;
    priceAlerts?: boolean;
  };
  costTargets?: {
    food: number;
    drinks: number;
    overall: number;
  };
  // Restaurant-wide default ingredient waste %. Absent = 0 (no waste).
  wastePercentage?: number;
  // Product keys starred on the utilization (waste) report — the Favorites tab.
  // Restaurant-wide, so the whole team sees the same list.
  favoriteProducts?: string[];
  // Same idea for the inventory Leakage tab — separate list because leakage rows are
  // keyed by INVENTORY key while the utilization report uses product keys.
  inventoryFavoriteProducts?: string[];
  // Same idea for the order-history Products page. Same product-key format as
  // `favoriteProducts` but its OWN list, so starring there doesn't flip the
  // utilization report onto its Favorites tab.
  productFavoriteProducts?: string[];
  // Day of week the weekly report email is sent. Stored as its own column server-side
  // but exposed here for the report-settings modal + profile chip. Default 'monday'.
  weeklyReportSendDay?: WeekDay;
}

export async function getRestaurantReportSettings(restaurantId: string): Promise<ReportSettings> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurants/${restaurantId}/report-settings`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch report settings: ${res.statusText}`);
  }

  const settings = await res.json();
  return settings.trackedDishes ? settings : { trackedDishes: { food: [], drinks: [] }, sections: {} };
}

export async function updateRestaurantReportSettings(
  restaurantId: string,
  settings: ReportSettings
): Promise<void> {
  const token = await getAccessToken();
  const body: any = { trackedDishes: settings.trackedDishes };
  if (settings.sections) {
    body.sections = settings.sections;
  }
  if (settings.costTargets) {
    body.costTargets = settings.costTargets;
  }
  if (settings.wastePercentage !== undefined) {
    body.wastePercentage = settings.wastePercentage;
  }
  // No `weeklyReportSendDay` here on purpose: since phase 8 the send day (and slot, and
  // language) is a super-admin setting, edited per owner account on Communication → Settings.
  // The field is still READ for the profile chip.
  const res = await fetch(`/api/restaurants/${restaurantId}/report-settings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || `Failed to update report settings: ${res.statusText}`);
  }
}

/**
 * Replace a favourite product-key list — the utilization report's (default), the
 * inventory Leakage tab's or the order-history Products page's. Its own endpoint
 * (not the admin-only report-settings PUT) so staff can star products too.
 */
export async function updateRestaurantFavoriteProducts(
  restaurantId: string,
  favoriteProducts: string[],
  scope: 'utilization' | 'inventory' | 'products' = 'utilization'
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurants/${restaurantId}/favorite-products`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ favoriteProducts, scope }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || `Failed to update favorite products: ${res.statusText}`);
  }
}
